import {
 DatasetBriefDto,
 FieldSearchResultDto,
 JsonNode,
 LastIngestionStatusDto,
 LastRunStatusDto,
 RuleDto,
 RuleInfoDto,
 TagReferenceDto,
 TimeSeriesPoint,
 UserDto,
} from '@/api';
import { Component } from 'vue';

/**
 * Run status values
 * Merge multiple enums into one, for better type checking
 */
export const RunStatusValues = {
 ...LastRunStatusDto.status, ...RuleInfoDto.lastRunStatus, ...LastIngestionStatusDto.status, ...RuleInfoDto.ruleStatus,
};
export type RunStatusTypes = LastRunStatusDto.status | RuleInfoDto.lastRunStatus | LastIngestionStatusDto.status | RuleInfoDto.ruleStatus;

/**
 * Aggregate essential monitor data
 * from RuleDto, RuleInfoDto, RuleDetailsDto & RuleCatalogAssetDtoV2
 */
export type MonitorAggregatedData = {
  createdBy?: UserDto | undefined;
  criticality?: number;
  creationMethod?: RuleDto.creationMethod;
  datasets?: DatasetBriefDto[];
  description?: string | undefined;
  hasAiRecommendations: boolean;
  id: string;
  // lastRunStatus?: RuleInfoDto.lastRunStatus | undefined;
  lastRunTimestamp?: number | undefined;
  lastWeekStatuses?: LastRunStatusDto[] | undefined;
  monitoredDatasetFields: FieldSearchResultDto[];
  name: string;
  ruleParams?: JsonNode | undefined;
  ruleStatus?: RuleInfoDto.ruleStatus;
  ruleTemplateName: string;
  schedule?: string | undefined;
  sourcePlatform: RuleInfoDto.sourcePlatform;
  tags?: TagReferenceDto[] | undefined;
  terms?: TagReferenceDto[] | undefined;
}

export enum MonitorModeEnum {
  FULL_DATA_SCAN = 'full_data_scan',
  INCREMENTAL_SCAN = 'incremental_scan',
  UNKNOWN = 'unknown',
}

export enum MonitorHeaderDisplayMode {
  LIST = 'list',
  OVERVIEW = 'overview',
}

/**
 * Dataset extended type with datasetFields
 */
export type MonitoredDatasetLightDto = {
  datasetFields: Array<FieldSearchResultDto>;
} & DatasetBriefDto;

/**
 * Graph point extended with value
 */
export type TimeSeriesPointWithValue = {
  value?: number; // Duplicated y 'value' for backward compatibility
} & TimeSeriesPoint;

/**
 * Filters tab type for datapoints table
 */
export type DatapointsTableFiltersTab = {
  key: string;
  name: string;
  disabled: boolean;
  count?: number;
}

/**
 * Graph configuration according to graph type
 */
export type GraphTypeConfiguration = {
  graphType: string;
  component: Component;
  valueFormatter: (value: number) => string;
  yMin?: number;
  yMax?: number;
  yInterval?: number;
}

export enum RuleRunTemplate {
  SQL = 'SiffletSqlRule',
  COMPLETENESS = 'SiffletCompletenessRule',
  DUPLICATES = 'SiffletDuplicatesRule',
  FRESHNESS = 'SiffletFreshnessRule',
  METADATA_FRESHNESS = 'SiffletMetadataFreshnessRule',
  SCHEMA_CHANGE = 'SiffletSchemaChangeRule',
  NUMERICAL_TRANSFORMATION = 'SiffletNumericalTransformationRule',
  CUSTOM_METRICS = 'SiffletCustomMetricsRule',
  INTERLINKED_METRICS = 'SiffletInterlinkedMetricsRule',
  DISTRIBUTION = 'SiffletDistributionRule',
  COUNT_DUPLICATE = 'SiffletCountDuplicateRule',
  NOT_IN_LIST = 'SiffletNotInListRule',
  COUNT_NULL_ROWS = 'SiffletCountNullRowsRule',
  UNIQUE = 'SiffletUniqueRule',
  REFERENTIAL_INTEGRITY = 'SiffletReferentialIntegrityRule',
  RANGE = 'SiffletRangeRule',
  IS_EMAIL = 'SiffletIsEmailRule',
  IS_PHONE_NUMBER = 'SiffletIsPhoneNumberRule',
  IS_UUID = 'SiffletIsUuidRule',
  MATCHES_REGEX = 'SiffletMatchesRegexRule',
  NO_CODE = 'SiffletNoCodeRule',
}

export type RuleTemplateName = `${RuleRunTemplate}`;
